import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, TextField, Checkbox, FormControlLabel,
  Select, MenuItem, FormControl
} from '@mui/material';
import NatalChartSVG from './NatalChartSVG';
import LOCATIONS from '../constants/locations';

const NatalLabel = ({ children }) => (
  <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap', minWidth: 70, textAlign: 'right' }}>{children}</Typography>
);
NatalLabel.propTypes = { children: PropTypes.node.isRequired };

const DateField = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <NatalLabel>Date</NatalLabel>
    <TextField size="small" type="date" value={value} onChange={e => onChange(e.target.value)} sx={{ width: 150 }} />
  </Box>
);
DateField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const TimeField = ({ value, onChange, tz }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <NatalLabel>Time</NatalLabel>
    <TextField size="small" type="time" value={value} onChange={e => onChange(e.target.value)} sx={{ width: 130 }} />
    <Typography sx={{ fontSize: '0.95rem', color: '#555' }}>{tz || 'EST'}</Typography>
  </Box>
);
TimeField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, tz: PropTypes.string };

const LonNatalField = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <NatalLabel>Longitude</NatalLabel>
    <TextField size="small" type="number" value={value}
      onChange={e => {
        const v = parseFloat(e.target.value);
        if (e.target.value === '' || e.target.value === '-') { onChange(e.target.value); return; }
        if (!isNaN(v)) onChange(String(Math.min(180, Math.max(-180, v))));
      }}
      inputProps={{ min: -180, max: 180, step: 0.01 }} sx={{ width: 130 }} />
  </Box>
);
LonNatalField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const LatField = ({ value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <NatalLabel>Latitude</NatalLabel>
    <TextField size="small" type="number" value={value}
      onChange={e => {
        const v = parseFloat(e.target.value);
        if (e.target.value === '' || e.target.value === '-') { onChange(e.target.value); return; }
        if (!isNaN(v)) onChange(String(Math.min(90, Math.max(-90, v))));
      }}
      inputProps={{ min: -90, max: 90, step: 0.01 }} sx={{ width: 130 }} />
  </Box>
);
LatField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const MOAGroup7Tab = ({ natal: propNatal, onNatalChange, chartData: propChartData, onChartDataChange }) => {
  const { api, token } = useAuth();
  const [localNatal, setLocalNatal] = useState({ date: '', time: '', longitude: '-74.01', latitude: '40.71', utcOffset: -5, tz: 'EST' });
  const natal = propNatal || localNatal;
  const setNatal = onNatalChange || setLocalNatal;
  const [natalError, setNatalError] = useState('');
  const [localChartData, setLocalChartData] = useState(null);
  const chartData = propChartData !== undefined ? propChartData : localChartData;
  const setChartData = onChartDataChange || setLocalChartData;
  const [drawAspectLines, setDrawAspectLines] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('New York');

  const setNatalField = (field, val) => setNatal(n => ({ ...n, [field]: val }));

  const handleLocationChange = (locLabel) => {
    setSelectedLocation(locLabel);
    const loc = LOCATIONS.find(l => l.label === locLabel);
    if (loc) {
      setNatal(n => ({ ...n, longitude: loc.lon, latitude: loc.lat, utcOffset: loc.utcOffset, tz: loc.tz }));
    }
  };

  const handleSet = async () => {
    const { date, time, longitude, latitude } = natal;
    if (!date || !time || longitude === '' || latitude === '') {
      setNatalError('All four fields (Date, Time, Longitude, Latitude) are required.');
      return;
    }
    setNatalError('');
    try {
      const res = await api.post('/api/natal-chart', { date, time, longitude: Number(longitude), latitude: Number(latitude), utcOffset: natal.utcOffset ?? -5 }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.data && !res.data.error) {
        setChartData(res.data);
      } else {
        setNatalError(res.data?.error || 'Unknown error');
      }
    } catch (err) {
      setNatalError(err.response?.data?.error || err.message);
    }
  };

  return (
    <Box sx={{ pt: 6, pb: 1, px: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Chart fills the area */}
      <NatalChartSVG chartData={chartData} drawAspectLines={drawAspectLines}>
        {/* Center panel: controls or just the checkbox */}
        {!drawAspectLines && (
          <>
            {/* Row 1: Date + Time */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, width: '100%', px: 1 }}>
              <DateField    value={natal.date}      onChange={v => setNatalField('date', v)} />
              <TimeField    value={natal.time}      onChange={v => setNatalField('time', v)} tz={natal.tz} />
            </Box>
            {/* Row 2: Longitude + Latitude */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, width: '100%', px: 1, mt: 1.5 }}>
              <LonNatalField value={natal.longitude} onChange={v => setNatalField('longitude', v)} />
              <LatField     value={natal.latitude}  onChange={v => setNatalField('latitude', v)} />
            </Box>
            {/* Row 3: Location + SET */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, width: '100%', px: 1, mt: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NatalLabel>Location</NatalLabel>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select value={selectedLocation} onChange={e => handleLocationChange(e.target.value)}>
                    {LOCATIONS.map(l => <MenuItem key={l.label} value={l.label}>{l.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button variant="contained" size="small" onClick={handleSet}
                  sx={{ minWidth: 64, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}>
                  SET
                </Button>
              </Box>
            </Box>
            {natalError && (
              <Typography sx={{ fontSize: '0.85rem', color: 'red', mt: 0.5 }}>{natalError}</Typography>
            )}
          </>
        )}
        {/* Draw Aspect Lines checkbox — always visible at bottom of center */}
        <Box sx={{ mt: 'auto' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={drawAspectLines}
              onChange={e => setDrawAspectLines(e.target.checked)} />}
            label={<Typography sx={{ fontSize: '0.95rem' }}>Draw Aspect Lines</Typography>}
          />
        </Box>
      </NatalChartSVG>

      {/* What this tab is for. It collects a natal moment and nothing else, which is not obvious
          from a chart and four fields — the studies against it are chosen on the next tab. */}
      <Typography sx={{ mt: 2, fontSize: '0.95rem', color: '#666', textAlign: 'center', maxWidth: 720 }}>
        Specify the first trade date/time or company incorporation time to study transit aspects to
        the natal date-time planets in this dialog and then go to the next tab and specify the
        studies required.
      </Typography>
    </Box>
  );
};

MOAGroup7Tab.propTypes = {
  natal: PropTypes.object,
  onNatalChange: PropTypes.func,
  chartData: PropTypes.object,
  onChartDataChange: PropTypes.func,
};

export default MOAGroup7Tab;
